import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/config";
import { createF24Riga, F24_SEZIONI, F24_CATEGORIE } from "../utils/f24Schema";

const parseF24PdfCallable = httpsCallable(functions, "parseF24Pdf");

const CATEGORIE_VALIDE = Object.values(F24_CATEGORIE);

// Converte un File in base64 puro (senza il prefisso "data:...;base64,").
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const commaIdx = result.indexOf(",");
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () => reject(new Error("Impossibile leggere il file."));
    reader.readAsDataURL(file);
  });
}

// Converte in numero accettando sia il formato JS ("1234.56") sia quello
// italiano ("1.362,50" → 1362.5). Ritorna 0 solo se il valore non è parsabile.
function toNumber(value) {
  // Path veloce: valore già numerico.
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (value === null || value === undefined) return 0;

  // Ripulisce simboli di valuta e spazi (inclusi NBSP e narrow NBSP).
  const raw = String(value).replace(/[\s  €]/g, "");
  if (!raw) return 0;

  const dotCount = (raw.match(/\./g) || []).length;
  let normalized;

  if (raw.includes(",")) {
    // Il separatore che appare per ultimo è quello decimale:
    // "1.362,50" (italiano) vs "1,362.50" (inglese).
    normalized =
      raw.lastIndexOf(",") > raw.lastIndexOf(".")
        ? raw.replace(/\./g, "").replace(",", ".")
        : raw.replace(/,/g, "");
  } else if (dotCount > 1) {
    // Più punti e nessuna virgola: sono separatori di migliaia ("1.362.500").
    normalized = raw.replace(/\./g, "");
  } else if (/^\d+\.\d{3}$/.test(raw)) {
    // Un solo punto seguito da esattamente 3 cifre ("1.500"): negli importi F24
    // i decimali sono sempre 2, quindi è un separatore di migliaia.
    normalized = raw.replace(".", "");
  } else {
    // Un solo punto o nessuno: già in formato JS ("1234.56", "1234").
    normalized = raw;
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

function toNullableString(value) {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return s ? s : null;
}

// Normalizza la risposta del modello nel draft atteso da F24Modal:
// riempie i default di ogni riga e valida i campi enum.
function toF24Draft(extracted) {
  const currentYear = new Date().getFullYear();
  const anno = Number(extracted.annoRiferimento) || currentYear;

  const righe = (Array.isArray(extracted.righe) ? extracted.righe : []).map((r) =>
    createF24Riga({
      sezione: r.sezione === F24_SEZIONI.INPS ? F24_SEZIONI.INPS : F24_SEZIONI.ERARIO,
      codiceTributo: (r.codiceTributo || "").toString().trim(),
      rateazione: toNullableString(r.rateazione),
      periodoRiferimentoDa: toNullableString(r.periodoRiferimentoDa),
      periodoRiferimentoA: toNullableString(r.periodoRiferimentoA),
      importoDebito: toNumber(r.importoDebito),
      importoCredito: toNumber(r.importoCredito),
      codiceSede: toNullableString(r.codiceSede),
      causaleContributo: toNullableString(r.causaleContributo),
    })
  );

  const categoria = CATEGORIE_VALIDE.includes(extracted.categoria)
    ? extracted.categoria
    : F24_CATEGORIE.ALTRO;

  return {
    categoria,
    descrizione: (extracted.descrizione || "").toString().trim(),
    dataScadenza: (extracted.dataScadenza || "").toString().trim(),
    annoRiferimento: anno,
    anno,
    note: (extracted.note || "").toString().trim(),
    righe,
  };
}

/**
 * Carica un PDF F24, lo fa analizzare da Claude (Cloud Function parseF24Pdf)
 * e restituisce un draft pronto per F24Modal.
 * @param {File} file PDF del modello F24
 * @returns {Promise<object>} draft F24 (categoria, descrizione, dataScadenza, righe[], ...)
 */
export async function importF24FromPdf(file) {
  if (!file) throw new Error("Nessun file selezionato.");
  if (file.type && file.type !== "application/pdf") {
    throw new Error("Il file deve essere un PDF.");
  }
  // ~6 MB: stesso limite applicato lato Cloud Function.
  if (file.size > 6 * 1024 * 1024) {
    throw new Error("PDF troppo grande (max 6 MB).");
  }

  const fileDataBase64 = await fileToBase64(file);
  const result = await parseF24PdfCallable({ fileDataBase64, fileName: file.name });
  const extracted = result?.data?.data;

  if (!extracted || !Array.isArray(extracted.righe) || extracted.righe.length === 0) {
    throw new Error("Impossibile leggere il PDF.");
  }

  return toF24Draft(extracted);
}
